// The money analysis (roadmap 11.P2–P5): "Megvehetem?", the AI's advice and reduction plan, the next cycle, where the
// money goes, the goals and the big plan, the subscriptions (one tap: cancelled), the loans, the rented flat, the net
// worth. The numbers come from the engine; the advice is the AI's from those numbers.
import { useCallback, useEffect, useState } from 'react';
import { api, onChange } from '../api';
import { day, ft, Label, Panel } from '../ui';

const LVL: Record<string, string> = { red: 'coral', yellow: 'amber', green: 'mint', info: 'cyan' };

export function CanIBuy() {
  const [price, setPrice] = useState('');
  const [item, setItem] = useState('');
  const [r, setR] = useState<any>(null);
  const ask = () => api('/api/money/can-i-buy', { price: Number(price.replace(/\s/g, '')), item: item || null }, { quiet: true }).then(setR).catch((e) => setR({ verdict: 'unknown', message: e.message }));
  return (
    <Panel c={r ? LVL[r.verdict === 'unknown' ? 'info' : r.verdict] : 'violet'}><Label c="violet">Megvehetem?</Label>
      <form className="j-row" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input className="j-input" value={item} onChange={(e) => setItem(e.target.value)} placeholder="mit (pl. cipő)" />
        <input className="j-input" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="ár (Ft)" style={{ maxWidth: 130 }} />
        <button className="j-btn j-btn--primary">Mehet?</button>
      </form>
      {r && <p className={`verdict verdict--${r.verdict}`}>{r.verdict === 'green' ? '✅ ' : r.verdict === 'yellow' ? '⚠️ ' : r.verdict === 'red' ? '⛔ ' : ''}{r.message}</p>}
    </Panel>
  );
}

function Advice() {
  const [a, setA] = useState<any>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api('/api/money/advice').then((x) => setA(x && Object.keys(x).length ? x : null)).catch(() => setA(null)), []);
  useEffect(() => { load(); }, [load]);
  const fresh = () => { setBusy(true); setErr(''); api('/api/money/advice', {}).then(setA).catch((e) => setErr(e.message)).finally(() => setBusy(false)); };
  const apply = () => api('/api/money/advice/apply', {}).then(() => setErr('A terv keretként beállítva.')).catch((e) => setErr(e.message));
  const adv = a?.advice;
  return (
    <Panel c="amber"><Label c="amber">Jarvis tanácsa{a?.day ? ` · ${day(a.day)}` : ''}</Label>
      {adv ? <>
        <p>{adv.summary}</p>
        {adv.tips.map((t: any, i: number) => (
          <div key={i} className="j-slot" style={{ '--c': `var(--${LVL[t.level]})` } as any}>
            <div className="t" style={{ color: `var(--${LVL[t.level]})` }}>●</div>
            <div><div><b>{t.title}</b>{t.money ? <span className="muted small"> · {ft(t.money)}/hó</span> : null}</div><div className="w">{t.text}</div></div>
          </div>
        ))}
        {adv.plan?.needed && adv.plan.items.length > 0 && <>
          <h3 className="small">Csökkentési terv · havi {ft(adv.plan.target_cut)}</h3>
          {adv.plan.items.map((p: any) => (
            <div key={p.category} className="j-slot"><div className="t">{p.category}</div><div><div>{ft(p.now)} → <b>{ft(p.target)}</b></div><div className="w">{p.how}</div></div></div>
          ))}
          <button className="j-btn" onClick={apply}>A terv legyen a keret</button>
        </>}
      </> : <p className="small muted">Még nincs tanács. A számaidból az AI ad konkrét tanácsot (vasárnaponként magától is).</p>}
      <div className="j-row"><button className="j-btn" disabled={busy} onClick={fresh}>{busy ? 'Gondolkodik…' : 'Friss tanács most'}</button></div>
      {err && <p className="small">{err}</p>}
    </Panel>
  );
}

export function MoneyInsights() {
  const [days, setDays] = useState(90);
  const [variable, setVariable] = useState(false);
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(() => api(`/api/money/analysis?days=${days}&variable=${variable ? 1 : 0}`).then(setD).catch((e) => setErr(e.message)), [days, variable]);
  useEffect(() => { load(); return onChange(load); }, [load]);
  const cancel = (s: any) => api('/api/money/config/cancelled_sub', { name: s.merchant, pattern: s.key.split('|')[1], cancelled_on: new Date().toISOString().slice(0, 10),
    charge_ok_until: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10) }).then(load).catch((e) => setErr(e.message));
  if (!d) return <div className="j-stack"><CanIBuy /><Advice />{err ? <p className="err">{err}</p> : <p className="muted">Betöltés…</p>}</div>;
  const f = d.forecast, s = d.spending, g = d.goals, nw = d.netWorth;
  const max = Math.max(1, ...s.categories.map((c: any) => c.monthlyAvg));
  return (
    <div className="j-stack">
      <CanIBuy />
      <Advice />
      <Panel c={f.result < 0 ? 'coral' : 'mint'}><Label c={f.result < 0 ? 'coral' : 'mint'}>Következő ciklus · {day(f.start)}–{day(f.end)}</Label>
        <h2 className="j-num big-num">{f.result >= 0 ? '+' : ''}{ft(f.result)}</h2>
        <p className="small">bevétel {ft(f.income)} − fix {ft(f.fixed)} − napi költés {ft(f.variable)} ({f.variableBasis}){f.plannedOut ? ` − tervezett ${ft(f.plannedOut)}` : ''}{f.plannedIn ? ` + ${ft(f.plannedIn)}` : ''}</p>
        {f.planned.length > 0 && <p className="small muted">{f.planned.map((p: any) => `${day(p.due)} ${p.name} ${p.kind === 'in' ? '+' : '−'}${ft(p.amount)}`).join(' · ')}</p>}
        {f.sinking.monthly > 0 && <p className="small">Az éves tételekre havonta félreteendő: {ft(f.sinking.monthly)} ({f.sinking.items.map((x: any) => x.name).join(', ')})</p>}
      </Panel>
      <Panel c="cyan"><Label c="cyan">Hová megy a pénz</Label>
        <div className="j-row">
          {[30, 90, 180, 365].map((n) => <button key={n} className={`j-chip ${days === n ? 'on' : ''}`} onClick={() => setDays(n)}>{n} nap</button>)}
          <button className={`j-chip ${variable ? 'on' : ''}`} onClick={() => setVariable(!variable)}>csak a változó</button>
        </div>
        <p className="small">Összesen {ft(s.total)}, havonta átlagosan {ft(s.monthlyAvg)}.</p>
        {s.categories.map((c: any) => (
          <div key={c.category} className="ana-row" onClick={() => setOpen(open === c.category ? null : c.category)}>
            <div className="ana-head"><span>{c.category}{c.fixed ? <span className="muted small"> · fix</span> : null}</span><span className="j-num">{ft(c.monthlyAvg)}/hó · {c.share}%</span></div>
            <div className="ana-bar"><i style={{ width: `${(c.monthlyAvg / max) * 100}%` }} /></div>
            {open === c.category && <div className="small muted">{c.places.map((p: any) => `${p.name} ${ft(p.amount)}`).join(' · ')}</div>}
          </div>
        ))}
      </Panel>
      <Panel c="mint"><Label c="mint">Célok és nagy terv</Label>
        {g.items.length ? g.items.map((it: any) => (
          <div key={`${it.source}${it.id}`} className="j-slot">
            <div className="t">{it.due ? day(it.due) : '–'}</div>
            <div><div><b>{it.name}</b> · {ft(it.have)} / {ft(it.amount)} ({it.percent}%)</div>
              <div className="w">{it.missing > 0 ? `hiányzik ${ft(it.missing)}${it.monthly ? `, havi ${ft(it.monthly)} kellene` : ''}${it.eta ? ` · a mostani ütemmel ${day(it.eta)}` : ''}${it.onTrack === false ? ' – nem jön ki időre' : ''}` : 'megvan'}</div></div>
          </div>
        )) : <p className="small muted">Nincs aktív cél. A Beállításban vehetsz fel.</p>}
        <p className="small">Megtakarítás: {ft(g.savings)} · minden határidőhöz havi {ft(g.requiredMonthly)} kellene · a mostani havi eredményed {g.pace != null ? ft(g.pace) : '–'}.</p>
      </Panel>
      <Panel c="violet"><Label c="violet">Előfizetések, rendszeres levonások</Label>
        {d.subscriptions.map((x: any) => (
          <div key={x.key} className="j-slot">
            <div className="t">{x.period}</div>
            <div><div>{x.merchant} · {ft(x.monthly)}/hó{x.cancelled ? <b className="coral"> · lemondva</b> : x.stale ? <span className="muted small"> · rég nem vont</span> : ''}</div>
              <div className="w">utoljára {day(x.lastDate)} · {x.count}×{x.inFixed ? ' · fix tételként rögzítve' : ''}</div>
              {!x.cancelled && <div className="task-acts"><button className="j-btn" onClick={() => cancel(x)}>Lemondtam</button></div>}</div>
          </div>
        ))}
        {!d.subscriptions.length && <p className="small muted">Nem találtam ismétlődő levonást.</p>}
      </Panel>
      <Panel c="coral"><Label c="coral">Hitelek</Label>
        {d.loans.map((l: any) => (
          <div key={l.id} className="j-slot"><div className="t">{l.pay_day}.</div>
            <div><div><b>{l.name}</b> · {ft(l.payment)}/hó{l.endsSoon ? <b className="mint"> · hamarosan lejár</b> : ''}</div>
              <div className="w">{l.principalNow != null ? `tőketartozás ${ft(l.principalNow)}` : 'tőke nincs megadva'}{l.monthsLeft != null ? ` · még ${l.monthsLeft} hónap` : ''}{l.principalPaid ? ` · azóta tőkében ${ft(l.principalPaid)}, kamatban ${ft(l.interestPaid)}` : ''}</div></div></div>
        ))}
      </Panel>
      <Panel c="amber"><Label c="amber">Kiadott lakás havonta</Label>
        {d.flat.map((m: any) => <div key={m.month} className="j-slot"><div className="t">{m.month}</div><div>+{ft(m.income)} − {ft(m.costs)} = <b className={m.net < 0 ? 'coral' : ''}>{ft(m.net)}</b></div></div>)}
      </Panel>
      <Panel c="cyan"><Label c="cyan">Nettó vagyon</Label>
        <h2 className="j-num big-num">{ft(nw.net)}</h2>
        <p className="small">készpénz, folyószámla {ft(nw.cash)} + megtakarítás {ft(nw.savings)} − számlatartozás {ft(nw.accountDebt)} − hitelek {ft(nw.loanDebt)}</p>
        {(d.accountIncome ?? []).map((a: any) => (
          <details key={a.id} className="sources"><summary>{a.name}: bevétel az elmúlt 12 hónapban {ft(a.total)}</summary>
            {a.months.filter((m: any) => m.amount).map((m: any) => <div key={m.month} className="small">{m.month} · {a.currency === 'HUF' ? ft(m.amount) : `${m.amount.toLocaleString('hu-HU')} ${a.currency} (${ft(m.huf)})`}</div>)}
          </details>
        ))}
      </Panel>
      {err && <p className="err">{err}</p>}
    </div>
  );
}
